import { DialogProps } from "@mui/material";
import { QUERY_PROGRESS_PENDING } from "core/consts";
import { QueryProgress } from "core/types";
import { ReactNode, useState } from "react";
import { RSButtonProps } from "../RSButton";

export type ConfirmationDialogProps<T> = {
  cancelButtonProps?: Partial<Omit<RSButtonProps, "onClick">>;
  cancelText?: string;
  componentContent?: ReactNode;
  confirmButtonProps?: Partial<Omit<RSButtonProps, "onClick">>;
  content?: string;
  headerIcon?: ReactNode;
  id: string;
  nodeContent?: ReactNode;
  onCancel: () => void;
  onSubmit: (data?: T) => void;
  opened: boolean;
  queryProgress?: QueryProgress;
  submitText?: string;
  sx?: DialogProps["sx"];
  title: ReactNode | string;
  useNewDesign?: boolean;
};

export type ConfirmDialogSettings<T> = Partial<
  Omit<ConfirmationDialogProps<T>, "onCancel" | "onSubmit" | "opened">
> & {
  keepModalOpenOnConfirm?: boolean;
  onCancel?: () => void;
  onSubmit?: (data?: T) => void | Promise<void>;
};

/**
 * Provides a way to use the `ConfirmationDialog` component programmatically,
 * similar to `window.confirm`, by calling a `confirm` function that returns a Promise.
 * This makes it easy to reuse a single confirmation dialog across multiple parts of a component.
 *
 * The `confirm` function accepts an optional `dialogSettings` parameter,
 * allowing customization of the dialog's content, buttons, and behavior for each call.
 *
 * When `keepModalOpenOnConfirm` is true:
 * - The modal will stay open after confirmation
 * - A spinner will be shown on the confirmation button in the modal
 * - The `onSubmit` callback should return a Promise
 * - The modal will only close when the Promise resolves successfully
 * - If the Promise rejects, the modal stays open and the spinner stops
 *
 * When `keepModalOpenOnConfirm` is false or undefined (default behavior):
 * - The modal closes immediately after confirmation
 * - The spinner is shown on the button that opened the modal (handled externally)
 *
 * @returns A tuple containing:
 *   - The props needed to control a `ConfirmationDialog` component.
 *   - A function (`confirm`) that triggers the dialog and returns a Promise with the user's choice.
 */
export function useConfirmationDialog<T = boolean>() {
  const [props, setProps] = useState<ConfirmationDialogProps<T>>({
    id: "send-request",
    onCancel: () => {},
    onSubmit: () => {},
    opened: false,
    title: "",
  });

  function confirm(
    dialogSettings: ConfirmDialogSettings<T> = {},
  ): Promise<T extends boolean ? boolean : T | undefined> {
    return new Promise((resolve, reject) => {
      const keepModalOpen = dialogSettings.keepModalOpenOnConfirm ?? false;

      function resolveDialog(result: T) {
        resolve(result as T extends boolean ? boolean : T | undefined);
        setProps((current) => ({
          ...current,
          opened: false,
          queryProgress: undefined,
        }));
      }

      function rejectDialog(error?: unknown) {
        reject(error);
        setProps((current) => ({
          ...current,
          queryProgress: undefined,
        }));
      }

      setProps((current) => ({
        ...current,
        ...dialogSettings,
        onCancel: () => {
          dialogSettings.onCancel?.();
          resolveDialog(undefined as T);
        },
        onSubmit: async (data?: T) => {
          if (keepModalOpen) {
            setProps((pendingProps) => ({
              ...pendingProps,
              queryProgress: QUERY_PROGRESS_PENDING,
            }));

            try {
              await dialogSettings.onSubmit?.(data);
              resolveDialog((data ?? true) as T);
            } catch (error) {
              rejectDialog(error);
            }
          } else {
            dialogSettings.onSubmit?.(data);
            resolveDialog((data ?? true) as T);
          }
        },
        opened: true,
        queryProgress: keepModalOpen ? undefined : dialogSettings.queryProgress,
      }));
    });
  }

  return [props, confirm] as const;
}
